import React, { useState } from 'react';
import { Share2, Edit, Trash2, Folder as FolderIcon } from 'lucide-react';
import PropTypes from 'prop-types';
import ShareModal from './ShareModal';
import { removeGroupFromFolder } from '../services/folderService';

const getTimestamp = (date) => {
  if (!date) return 0;
  if (typeof date.toMillis === 'function') return date.toMillis();
  if (date instanceof Date) return date.getTime();
  if (typeof date === 'number') return date;
  return 0;
};

const CardStack = ({ group, cards, onCardClick, onShareClick, currentUser, onMoveGroup }) => {
  if (typeof group !== 'object' || !group.id) {
    console.warn('CardStack received invalid group prop:', group);
  }
  const [showShareModal, setShowShareModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const filteredCards = cards.filter(card => {
    if (card.deleted) return false;
    
    // Check if card belongs to this group
    if (card.group === group.name) return true;
    if (card.groups?.includes(group.name)) return true;
    if (card.sharedGroups?.includes(group.name)) return true;
    
    return false;
  });
  const previewCards = filteredCards.slice(0, 3);
  const remainingCards = Math.max(0, filteredCards.length - 3);
  const hasSharedCards = filteredCards.some(card => card.isShared);

  const handleShareClick = (e) => {
    e.stopPropagation(); // Prevent card stack click
    setShowShareModal(true);
  };

  const handleFolderClick = (e) => {
    e.stopPropagation();
    if (group.folderId) {
      setShowRemoveConfirm(true);
    } else if (onMoveGroup) {
      onMoveGroup(group);
    }
  };

  const handleRemoveFromFolder = async () => {
    setRemoving(true);
    try {
      await removeGroupFromFolder(group.name);
      setShowRemoveConfirm(false);
      setRemoving(false);
      // Optionally, trigger a reload in parent via a callback or event
      window.location.reload(); // quick fix, ideally use a prop callback
    } catch (err) {
      setRemoving(false);
      setShowRemoveConfirm(false);
      alert('Failed to remove group from folder');
    }
  };

  return (
    <>
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow h-52 relative"
        onClick={() => onCardClick(group.name)}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
          </div>
          <div className="flex items-center gap-2">
            {hasSharedCards && (
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                Shared
              </span>
            )}
            <button
              onClick={handleShareClick}
              className="p-2 text-gray-600 hover:text-[#04b53a] hover:bg-gray-50 rounded-full transition-colors"
              title="Share group"
            >
              <Share2 className="w-5 h-5" />
            </button>
            {onMoveGroup && (
              <button
                onClick={handleFolderClick}
                className="p-1 text-gray-600 hover:text-[#04b53a] hover:bg-gray-50 rounded-full transition-colors"
                title={group.folderId ? 'Remove from Folder' : 'Move to Folder'}
              >
                <FolderIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex justify-center items-center h-24">
          <div className="relative flex">
            {previewCards.map((card, index) => (
              <div 
                key={card.id} 
                className="w-20 h-20 rounded-full overflow-hidden border-2 border-white shadow -ml-8 first:ml-0"
                style={{ zIndex: 3 - index }}
              >
                {card.imageUrl ? (
                  <img 
                    src={card.imageUrl} 
                    alt={`Card ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">{card.firstName?.charAt(0) || '?'}</span>
                  </div>
                )}
              </div>
            ))}
            
            {remainingCards > 0 && (
              <div className="text-sm text-gray-500 flex items-center ml-4">
                +{remainingCards} more
              </div>
            )}
          </div>
        </div>
      </div>

      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Remove from Folder?</h2>
            <p className="mb-4">Are you sure you want to remove <b>{group.name}</b> from its folder?</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-200 rounded"
                onClick={() => setShowRemoveConfirm(false)}
                disabled={removing}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded"
                onClick={handleRemoveFromFolder}
                disabled={removing}
              >
                {removing ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={(recipientId, _, withEditAccess) => {
          onShareClick(recipientId, group.name, withEditAccess);
          setShowShareModal(false);
        }}
        type="group"
        item={{ name: group.name, cards: filteredCards }}
        currentUser={currentUser}
      />
    </>
  );
};

CardStack.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    folderId: PropTypes.string
  }).isRequired,
  cards: PropTypes.array.isRequired,
  onCardClick: PropTypes.func.isRequired,
  onShareClick: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  onMoveGroup: PropTypes.func.isRequired
};

export default CardStack;